import React from 'react';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import {Visit} from '../models/visit.model';
import {MapPin, User} from 'lucide-react';
import {AppTableText} from '../../../Presentation/components/ui/Typography';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormat} from '../../../types/export.types';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';

interface VisitsTableProps {
  visits: Visit[];
  onVisitClick?: (visit: Visit) => void;
  // Pagination
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  exportable?: boolean;
  loading?: boolean;
}

const VisitsTable: React.FC<VisitsTableProps> = ({
  visits,
  onVisitClick,
  page = 1,
  pageSize = 20,
  totalCount = 0,
  onPageChange,
  onPageSizeChange,
  exportable = false,
  loading = false
}) => {
  // Export configuration
  const {createConfig} = useExportConfig<Visit>();

  const exportConfig = createConfig([
    {key: 'id', title: 'رقم الزيارة'},
    {key: 'store', title: 'اسم المتجر', formatter: ExportFormatters.storeName},
    {key: 'store', title: 'المدينة', formatter: ExportFormatters.storeCity},
    {key: 'store', title: 'المنطقة', formatter: ExportFormatters.storeRegion},
    {key: 'visitor', title: 'اسم الزائر', formatter: ExportFormatters.visitorName},
    {key: 'visitor', title: 'اسم المستخدم', formatter: ExportFormatters.visitorUsername},
    {key: 'status', title: 'حالة الزيارة', formatter: ExportFormatters.visitStatus},
    {key: 'visitDate', title: 'تاريخ الزيارة', formatter: ExportFormatters.dateTime},
    {key: 'createdAt', title: 'تاريخ الإنشاء', formatter: ExportFormatters.dateTime},
    {key: 'notes', title: 'ملاحظات'}
  ], 'زيارات', 'تصدير سجلات الزيارات', 'تصدير جميع سجلات الزيارات مع التفاصيل الكاملة');

  // Export hook
  const {exportData, isExporting, canExport, recordCount} = useTableExport({
    data: visits,
    config: exportConfig
  });

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    await exportData(format);
  };

  // Define table columns with proper error handling
  const columns: TableColumn<Visit>[] = [
    {
      key: 'id',
      title: 'رقم الزيارة',
      render: (visit) => (
        <AppTableText className="font-medium text-brand-600">
          #{visit?.id || 'غير محدد'}
        </AppTableText>
      )
    },
    {
      key: 'storeName',
      title: 'اسم المتجر',
      render: (visit) => (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-gray-400" />
          <AppTableText className="font-medium">{visit?.store?.storeName || 'غير محدد'}</AppTableText>
        </div>
      )
    },
    {
      key: 'userName',
      title: 'المسوق',
      render: (visit) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <AppTableText className="font-medium">{visit?.visitor?.fullName || 'غير محدد'}</AppTableText>
        </div>
      )
    },
    {
      key: 'brandName',
      title: 'العلامة التجارية',
      render: (visit) => (
        <AppTableText className="font-medium">{visit?.brand?.name || 'غير محدد'}</AppTableText>
      )
    },
    {
      key: 'visitDate',
      title: 'تاريخ الزيارة',
      render: (visit) => (
        <DateTimeDisplay dateString={visit?.visitDate || ''} />
      )
    },
    {
      key: 'status',
      title: 'الحالة',
      render: (visit) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${visit?.statusColor || 'text-gray-600 bg-gray-100'}`}>
          {visit?.statusText || 'غير محدد'}
        </span>
      )
    },
    {
      key: 'productsUpdated',
      title: 'المنتجات المحدثة',
      render: (visit) => (
        <AppTableText className="font-medium">
          {visit?.visitItems?.length || 0}
        </AppTableText>
      )
    },
    {
      key: 'imagesCount',
      title: 'الصور',
      render: (visit) => (
        <AppTableText>
          {visit?.visitImages?.length || 0}
        </AppTableText>
      )
    }
  ];

  // Row actions removed - using actions column instead

  // Filter functionality removed - not needed

  return (
    <div className="flex-1 min-h-[calc(100vh-80px)] flex flex-col">
      {/* Export Button */}
      {exportable && (
        <div className="mb-4 flex justify-end">
          <ExportButton
            onExport={handleExport}
            isExporting={isExporting}
            canExport={canExport}
            recordCount={recordCount}
            showDropdown={true}
          />
        </div>
      )}

      <div className="overflow-x-auto">
        <AppTable
          columns={columns}
          data={visits}
          rowKey={(visit, index) => `visit-${visit.id}-${index || 0}`}
          emptyText="لا توجد زيارات"
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          showPageSizeSelector={true}
          showPaginationInfo={true}
          loading={loading}
          loadingRows={pageSize || 20}
          loadingMessage="جاري تحميل الزيارات..."
          onRowClick={onVisitClick}
        />
      </div>
    </div>
  );
};

export default VisitsTable;
